import { Moment } from "moment";
import Utils from "./utils";
import * as _ from "lodash";
import { SyncDataModel, DataModel, SyncSingleDataModel } from "./models/syncModel";
import { CoreLogService } from "../services/log.service";
import { isArray } from "util";
import { CircularInjectionHelper } from "../services/circularInjectionHelper";
import { ProductDataModel4, ProductsSyncDataModel4 } from "./models";

//export interface IModelManager<T extends DataModel> {
//  readonly create_at: Moment;
//  readonly NeedsUpload: boolean;
//  get(key: number | string): T;
//}

export class ModelManager<T extends DataModel> {
  // All properties with whre starting with "_" like "_coreLogService" dose not stringify

  protected last_sync_at_ymd: number = Utils.MinYMD;
  public get LastSyncAt_ymd(): number {
    return this.last_sync_at_ymd;
  }
  protected last_sync_at_hms: number = Utils.MinHMS;
  public get LastSyncAt_hms(): number {
    return this.last_sync_at_hms;
  }
  public get LastSyncAt(): Moment {
    return Utils.GetMomentFromInt(this.last_sync_at_ymd, this.last_sync_at_hms);
  }

  protected _coreLogService: CoreLogService = null;
  protected _model_constructor: new () => T = null;
  /** ist vom typ {key: string|number, value: T} */
  protected data_models: {} = {};
  protected _needSave: boolean = false;
  //todo momentan ist es nicht im gebrauch
  //protected needs_upload: boolean = false;
  //public get NeedsUpload(): boolean { return this.needs_upload; }

  public constructor(ModelConstructor: new () => T, CoreLogService: CoreLogService) {
    if (Utils.IsNull(CoreLogService)) {
      this._coreLogService.error("ModelManager.constructor", "CoreLogService is null");
      throw new Error("CoreLogService is null");
    }
    this._coreLogService = CoreLogService;
    if (Utils.IsNull(ModelConstructor)) {
      this._coreLogService.error("ModelManager.constructor", "ModelConstructor is null");
      throw new Error("ModelConstructor is null");
    }
    this._model_constructor = ModelConstructor;
  }

  /** Achtung! darf nur durch ObjectObserver nach dem speichern aufgerufen Werden
   * @param key ist nur schutz vor falschem aufruf
   */
  public markSaved(key: number): void {
    if (key == 712468) {
      this._needSave = false;
    } else {
      this._coreLogService.error("ModelManager.markSaved", "key is wrong");
      throw new Error("key is wrong");
    }
  }

  /** ist ein Speichern notwendig (true wenn änderung vorgenommen wurde, mit markSaved wird es zurückgesetzt) */
  public needSave(): boolean {
    return this._needSave;
  }

  /** deserialize ModelManager from string
  * @param data Serialized object or array
  * @returns Deserialized object or array
  */
  public deserialize(data: string): void {
    const extension = JSON.parse(data);
    Utils.GetExtend(this, extension);
    this.deserializeType();
    // nach einem Desirializieren wird NeedSave zurückgesetzt
    this._needSave = false;
  }

  /** Casting the Models to their real ObjectType */
  protected deserializeType(): void {
    const me = this;
    const ModelPrototype = Object.getPrototypeOf(new me._model_constructor());
    for (const key in me.data_models) {
      if (me.data_models.hasOwnProperty(key)) {
        const model = me.data_models[key];
        if (Object.getPrototypeOf(model) !== ModelPrototype) {
          const deserializedModel = Utils.GetExtend(new me._model_constructor(), model);
          if (deserializedModel) {
            deserializedModel.DeserializeSubTypes();
          }
          me.data_models[key] = deserializedModel;
          // speichern ist nicht erforderlich, die Daten werden nur gecastet
        }
      }
    }
  }

  /** serialize ModelManager to string
   * @returns string for savin in data
   */
  public serialize(): string {
    const me = this;
    const replacer = (key: string, value: any): any => {
      // dose not stringify propertys where name/key starting with "_"
      if (key[0] === "_") {
        return undefined;
      }
      const obj: any = this;
      const propertyDescriptor = Object.getOwnPropertyDescriptor(obj, key);
      if (propertyDescriptor !== undefined && propertyDescriptor.get && !propertyDescriptor.set) {
        return undefined; // ignore getter without setter, becaus on reading thy also ignored
      }
      return value;
    };
    return JSON.stringify(me, replacer);
  }

  /** Checks if a DataModel exists with matched key */
  public contains(key: number | string): boolean {
    return !Utils.IsNull(this.data_models[key]);
  }

  /** Checks if a DataModel exists with matched filter */
  public containsByFilter(filter: (obj: T) => boolean): boolean {
    let contains = false;
    for (const key in this.data_models) {
      if (this.data_models.hasOwnProperty(key)) {
        if (filter(this.data_models[key])) {
          contains = true;
          break; // exit with first match
        }
      }
    }
    return contains;
  }

  /** return object by key or undefine !ATTENTION! object is a Copy */
  public get(key: number | string): T {
    let model = this.data_models[key];
    if (!Utils.IsNull(model)) {
      model = Utils.GetCopy(model);
    }
    return model;
  }

  /** return object array by filtering !ATTENTION! object is a Copy */
  public getByFilter(filter: (obj: T) => boolean): T[] {
    const me = this;
    const models: T[] = new Array();
    for (const key in me.data_models) {
      if (me.data_models.hasOwnProperty(key)) {
        const model = me.data_models[key];
        if (filter(model)) {
          models.push(Utils.GetCopy(model));
        }
      }
    }
    return models;
  }

  /** return all objects !ATTENTION! objects are Copies */
  public getAll(): T[] {
    const models: T[] = new Array();
    for (const key in this.data_models) {
      if (this.data_models.hasOwnProperty(key)) {
        const model = this.data_models[key];
        models.push(Utils.GetCopy(model));
      }
    }
    return models;
  }

  /** sets the Model, and marks it to syncronise with server */
  public set(model: T): void {
    this.setExternModel(Utils.GetCopy(model));
  }

  /** Achtung! darf nur in bestimmten verwaltungs aufgaben ausgeführt werden
   * sets the Model with no iPE status (no back synchronisation with server)
   * @param key ist nur schutz vor falschem aufruf
   */
  public setIntern(model: T, key: number): void {
    if (key == 712468) {
      this.setInternModel(Utils.GetCopy(model));
    } else {
      this._coreLogService.error("ModelManager.markSaved", "key is wrong");
      throw new Error("key is wrong");
    }
  }

  /** extending the model by object, full model, with all properties, is not need, and marks it to syncronise with server */
  public extendOverKey(key: number | string, model: T | any): void {
    const me = this;
    const old_model = <T>me.data_models[key];
    if (Utils.IsNull(old_model)) {
      me._coreLogService.error("ModelManager.extendOverKey", "model for extend not found", me.data_models, key);
      throw new Error("model for extend not found");
    }
    me.setExternModel(Utils.GetExtend(old_model, model));
  }

  /**
   * @param sync_data model from Server, they extends the model
   */
  public extendWithSyncData(sync_data: SyncDataModel): void {
    const me = this;
    if (me.LastSyncAt > sync_data.request_at) {
      me._coreLogService.error("ModelManager.extendWithSyncData", "extendWithSyncData SyncDataModel is older then local data", me.LastSyncAt, sync_data);
      throw new Error("extendWithSyncData SyncDataModel is older then local data");
    }
    // if it has no models, it must get at the next time again
    if (isArray(sync_data.models) && sync_data.models.length > 0) {
      me.last_sync_at_ymd = sync_data.request_at_ymd;
      me.last_sync_at_hms = sync_data.request_at_hms;
      me._needSave = true; // speichern wird erforderlich

      // ! Achtung bei producten ist auch eine Liste mit Product_IDs enthalten, welche nicht mehr bestandteil des sortiments sind
      if (sync_data instanceof ProductsSyncDataModel4) {
        if (sync_data.productsNotInSortiment.length > 0) {
          for (const key in me.data_models) {
            if (me.data_models.hasOwnProperty(key)) {
              const model = <ProductDataModel4>me.data_models[key];
              if (model.saleable && sync_data.productsNotInSortiment.indexOf(model.product_id) > -1) {
                // markierung als nicht verkaufsfähig (nicht deleted, denn die Abfragen benötigen es, aber sie sollen nicht im verkauf angeboten werden)
                model.saleable = false;
                // setze auch das change datum damit man weis, das es nicht mehr das inital übertrage product ist
                model.change_at_ymd = me.last_sync_at_ymd;
                model.change_at_hms = me.last_sync_at_hms;
                // kein weiteren internen status setzen, da er sonst auch bleibt, fals der artikel wieder ins sortiment kommt
              }
            }
          }
        }
      }

      sync_data.models.forEach(model => {
        me.extendWithSyncDataModel(model);
      });
    }
  }

  /**
   * @param sync_data model from Server, they extends the model
   * @param overwrite if true, it ignore LastSyncAt and request_at
   */
  public extendWithSyncSingleData<T extends DataModel>(sync_data: SyncSingleDataModel<T>, overwrite: boolean = false): void {
    const me = this;
    // todo mutex zum absichern ?
    if (me.LastSyncAt > sync_data.request_at && !overwrite) {
      me._coreLogService.error("ModelManager.extendWithSyncSingleData", "extendWithSyncSingleData SyncDataModel is older then local data", me.LastSyncAt, sync_data);
      throw new Error("extendWithSyncSingleData SyncDataModel is older then local data");
    }
    // if it has no models, it must get at the next time again
    if (!Utils.IsNull(sync_data.model)) {
      me.last_sync_at_ymd = sync_data.request_at_ymd;
      me.last_sync_at_hms = sync_data.request_at_hms;
      me._needSave = true; // speichern wird erforderlich

      me.extendWithSyncDataModel(sync_data.model);
    }
  }

  /** Achtung! darf nur in bestimmten verwaltungs aufgaben ausgeführt werden 
   * set DataModel from syncData 
   */
  protected extendWithSyncDataModel(new_model: DataModel): void {
    const me = this;
    const DM_Key = new_model.DM_Key;
    if (Utils.IsNull(DM_Key)) {
      me._coreLogService.error("ModelManager.extendWithSyncDataModel", "DM_Key of model is null", new_model);
      throw new Error("DM_Key of model is null");
    }

    let old_model = me.data_models[DM_Key];
    if (Utils.IsNull(old_model)) {
      old_model = new me._model_constructor();
    }
    const newModel = Utils.GetExtend(old_model, new_model);
    me.prepareSyncDataModel(newModel);
    me.setInternModel(newModel);
  }

  /** can be overwritten to prepare DataModel after Sync */
  protected prepareSyncDataModel(model: T): void {
    model.DM_iPE = 0; // no sync needs its from yourappserver
  }

  /** sets the Model, and marks it to syncronise with server */
  protected setExternModel(newModel: T): void {
    const me = this;
    newModel.DM_iPE = 1; // need sync needs its from yourappserver
    me.data_models[newModel.DM_Key] = newModel; // if exists, update else create new
    me._needSave = true; // speichern wird erforderlich
  }

  /** (only internal function) sets the Model, without marks it to syncronise with server */
  protected setInternModel(newModel: T): void {
    const me = this;
    me.data_models[newModel.DM_Key] = newModel; // if exists, update else create new
    me._needSave = true; // speichern wird erforderlich
  }

  /** Checks, if one or more Models exists who need a synchronisation with the YourAppServer */
  public hasUnsynced(): boolean {
    const me = this;
    for (const key in me.data_models) {
      if (me.data_models.hasOwnProperty(key)) {
        if (me.data_models[key].DM_iPE !== 0) {
          return true;
        }
      }
    }
    return false;
  }

  /** Gets Models who need a synchronisation with the YourAppServer
   * @returns Array with Items for syncronizing to YourAppServer
   */
  public getUnsynced(): T[] {
    const me = this;
    const unsynced_models = new Array<T>();
    for (const key in me.data_models) {
      if (me.data_models.hasOwnProperty(key)) {
        const model: T = me.data_models[key];
        if (model.DM_iPE !== 0) {
          unsynced_models.push(Utils.GetCopy(model));
        }
      }
    }
    return unsynced_models;
  }

  /** for overwriting to after main data load referenst data */
  public postLoadData(keys: (string | number)[], helper: CircularInjectionHelper): Promise<void> {
    // overwrite if need
    // dosen't called by children
    return Promise.resolve(); // do nothing in base
  }

}
